import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { format, eachDayOfInterval, subMonths, subWeeks } from 'date-fns';

const Insights = ({ taskDurations }) => {
  const [endDate, setEndDate] = useState(new Date());
  const [startDate, setStartDate] = useState(subWeeks(new Date(), 1));
  const [activeRange, setActiveRange] = useState('week');
  const [allTasks, setAllTasks] = useState([]);

  useEffect(() => {
    const fetchAllTasks = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/tasks`);
        setAllTasks(await res.json());
      } catch (e) { console.error(e); }
    };
    fetchAllTasks();
  }, []);

  const setDateRange = (range) => {
    const today = new Date();
    setEndDate(today);
    setActiveRange(range);

    switch (range) {
      case 'week':
        setStartDate(subWeeks(today, 1));
        break;
      case 'month':
        setStartDate(subMonths(today, 1));
        break;
      case '3months':
        setStartDate(subMonths(today, 3));
        break;
      case '6months':
        setStartDate(subMonths(today, 6));
        break;
      default:
        setStartDate(subWeeks(today, 1));
    }
  };

  const colors = ['#FFD700', '#90EE90', '#9370DB', '#FF6347'];

  return (
    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-md">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-lg font-semibold mb-4 sm:mb-0">Insights</h2>

        {/* Date Range Picker */}
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <div className="flex gap-2 items-center">
            <label className="text-sm text-gray-400">From:</label>
            <input
              type="date"
              value={format(startDate, 'yyyy-MM-dd')}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              className="bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-sm text-gray-400">To:</label>
            <input
              type="date"
              value={format(endDate, 'yyyy-MM-dd')}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              className="bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setDateRange('week')}
              className={`${activeRange === 'week' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'} text-white px-2 py-1 rounded text-xs transition-colors`}
            >
              1W
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`${activeRange === 'month' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'} text-white px-2 py-1 rounded text-xs transition-colors`}
            >
              1M
            </button>
            <button
              onClick={() => setDateRange('3months')}
              className={`${activeRange === '3months' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'} text-white px-2 py-1 rounded text-xs transition-colors`}
            >
              3M
            </button>
            <button
              onClick={() => setDateRange('6months')}
              className={`${activeRange === '6months' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'} text-white px-2 py-1 rounded text-xs transition-colors`}
            >
              6M
            </button>
          </div>
        </div>
      </div>

      {/* Show date range info */}
      <div className="mb-4 text-sm text-gray-400 text-center">
        Showing data from {format(startDate, 'MMM d, yyyy')} to {format(endDate, 'MMM d, yyyy')}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {allTasks.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-400">
              <span className="text-4xl mb-2 block">📊</span>
              <p className="text-lg mb-2">No tasks to analyze</p>
              <p className="text-sm text-gray-500">Create some tasks and track your progress to see insights!</p>
            </div>
          </div>
        ) : (
          (() => {
            const categoryMap = {
              'Daily Class': t => t.toLowerCase().includes('daily class') || t.toLowerCase().includes('quick class'),
              'Newspaper': t => t.toLowerCase().includes('newspaper') || t.toLowerCase().includes('current affairs') || t.toLowerCase().includes('weekly ca'),
              'Answer Writing': t => t.toLowerCase().includes('answer writing') || t.toLowerCase().includes('pyq'),
              'Revision': t => t.toLowerCase().includes('revision'),
              'Topper Answer': t => t.toLowerCase().includes('topper'),
            };

            const grouped = {};
            Object.keys(categoryMap).forEach(cat => { grouped[cat] = []; });
            allTasks.forEach(task => {
              for (const [cat, matcher] of Object.entries(categoryMap)) {
                if (matcher(task.title)) {
                  grouped[cat].push(task);
                  break;
                }
              }
            });

            const activeCategories = Object.entries(grouped).filter(([, tasks]) => tasks.length > 0);

            return activeCategories.map(([category, tasks], index) => {
              const dates = eachDayOfInterval({ start: startDate, end: endDate });
              const mergedData = dates.map(date => {
                const dateISO = format(date, 'yyyy-MM-dd');
                const totalDuration = tasks.reduce((sum, t) => sum + (taskDurations[`${t._id}_${dateISO}`] || 0), 0);
                return { date: dateISO, duration: totalDuration };
              });

            const totalDuration = mergedData.reduce((sum, d) => sum + d.duration, 0);
            const activeDays = mergedData.filter(d => d.duration > 0).length;

            const chartData = {
              labels: mergedData.map(d => format(new Date(d.date), 'MMM d')),
              datasets: [
                {
                  label: category,
                  data: mergedData.map(d => d.duration),
                  borderColor: colors[index % colors.length],
                  borderWidth: 2,
                  fill: false,
                  tension: 0.1,
                },
              ],
            };

          const chartOptions = {
            responsive: true,
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Date',
                  color: '#9CA3AF'
                },
                ticks: {
                  color: '#9CA3AF',
                  maxTicksLimit: 10, // Limit number of date labels to avoid crowding
                  callback: function(value, index) {
                    // Show every nth label to avoid crowding
                    const step = Math.ceil(mergedData.length / 8);
                    return index % step === 0 ? this.getLabelForValue(value) : '';
                  }
                },
                grid: {
                  color: '#374151'
                }
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Minutes',
                  color: '#9CA3AF'
                },
                ticks: {
                  color: '#9CA3AF'
                },
                grid: {
                  color: '#374151'
                }
              },
            },
            plugins: {
              legend: {
                labels: {
                  color: '#9CA3AF'
                }
              }
            },
            elements: {
              point: {
                radius: 3,
                hoverRadius: 6
              }
            }
          };

            return (
              <div key={category} className="mb-8 border-2 border-gray-700 p-4 rounded-xl">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-md font-semibold">{category}</h3>
                  <div className="text-right text-sm text-gray-400">
                    <div>Total: {totalDuration} mins</div>
                    <div>Active days: {activeDays}</div>
                    <div>Avg: {activeDays > 0 ? Math.round(totalDuration / activeDays) : 0} mins/day</div>
                  </div>
                </div>
                <Line data={chartData} options={chartOptions} />
              </div>
            );
            });
          })()
        )}
      </div>
    </div>
  );
};

export default Insights;